import { closePhotoViewer, PhotoTrigger } from '../components/photo-viewer.tsx';
import { Alert, Button, LoadingBlock, Select } from '../components/ui.tsx';
import { captureChatOutbox, type ChatPending } from '../state/job-chat-outbox.ts';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { WorkPage, WorkSection, WorkEmpty } from '../components/work-page.tsx';
import { chatCopy, chatNotificationText } from '../../shared/job-chat.ts';
import '../components/job-chat-navigation.css';
import { formatInstantDateTime } from '../i18n/datetime.ts';
type Message = {
  id: string;
  body: string;
  system_code: keyof typeof chatCopy.en | null;
  actor_kind: string;
  display_name?: string;
  customerVisible?: boolean;
};
type Chat = {
  id: string;
  hasOlder?: boolean;
  visitName: string;
  appointmentId: string;
  startsAt: string;
  /** The visit's own saved zone; absent from servers older than this field. */
  timezone?: string;
  state: string;
  writable: boolean;
  customerParticipation?: { enabled: boolean; version: number; needsReview: boolean; recipientName?: string | null };
  unreadCount: number;
  needsReply: boolean;
  notifications?: {
    event_name: string;
    status: string;
    created_at: string;
    reason_code?: string | null;
    retryable?: boolean;
  }[];
  feedback: { stars: number; private_feedback: string }[];
  messages: Message[];
  attachments: { id: string; message_id: string; promoted_file_id: string | null }[];
};
export default function ChatsPage() {
  const session = useSession();
  const [params] = useSearchParams();
  // Retire the entire view, including in-flight actions, at the same privacy
  // boundaries as the outbox. A visit change is a new navigation context too.
  const scope = JSON.stringify([
    session.user?.id,
    session.company?.id,
    session.currentLocation?.id,
    session.user?.role,
    [...(session.user?.permissions ?? [])].sort(),
    session.company?.effectivePlan,
    [...(session.company?.planFeatures ?? [])].sort(),
    session.company?.accessLocked,
    session.impersonatedBy,
    params.get('visitId'),
  ]);
  return <ScopedChatsPage key={scope} />;
}

function ScopedChatsPage() {
  const { i18n } = useTranslation();
  const copy = chatCopy[i18n.language.startsWith('es') ? 'es' : 'en'];
  const es = i18n.language.startsWith('es');
  const session = useSession();
  // Visits read at the hour they happen where the work is, not on this laptop:
  // the visit's own saved zone, else the location's.
  const fallbackZone = session.currentLocation?.timezone ?? session.company?.timezone;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const visitId = params.get('visitId');
  const [listCursor, setListCursor] = useState('');
  const [previousCursors, setPreviousCursors] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const mounted = useRef(false);
  const listRequest = useRef(0);
  const retryLoad = useRef(() => {});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState('all');
  const [cap, setCap] = useState<{ enabled: boolean; plan: string; monthlyUsed?: number } | null>(null),
    [items, setItems] = useState<Chat[]>([]),
    [selected, setSelected] = useState(''),
    [error, setError] = useState(''),
    [body, setBody] = useState(''),
    [photo, setPhoto] = useState<File | null>(null),
    [busy, setBusy] = useState(false);
  const baseListPath = visitId
    ? `/job-chats?visitId=${encodeURIComponent(visitId)}`
    : listCursor
      ? `/job-chats?beforeChat=${encodeURIComponent(listCursor)}`
      : '/job-chats';
  const listPath = selected
    ? `${baseListPath}${baseListPath.includes('?') ? '&' : '?'}selectedChatId=${encodeURIComponent(selected)}`
    : baseListPath;
  const currentPath = useRef(listPath);
  currentPath.current = listPath;
  const pending = useRef<ChatPending | null>(null);
  const [queued, setQueued] = useState<ChatPending[]>([]);
  const sending = useRef(false);
  const draftAudience = useRef(new Map<string, number | undefined>());
  const drafts = useRef(new Map<string, { body: string; photo: File | null }>());
  const gridRef = useRef<HTMLDivElement>(null);
  function focusThread() {
    if (!window.matchMedia?.('(max-width: 56rem)').matches) return;
    const thread = gridRef.current?.querySelector<HTMLElement>('.work-section');
    const heading = thread?.querySelector('h2');
    heading?.setAttribute('tabindex', '-1');
    heading?.focus({ preventScroll: true });
    thread?.scrollIntoView({ block: 'start' });
  }
  function returnToChats() {
    const row = document.getElementById(`job-chat-row-${selected}`);
    const target = row ?? gridRef.current?.querySelector('select');
    target?.focus({ preventScroll: true });
    target?.scrollIntoView({ block: 'start' });
  }
  function selectChat(id: string) {
    if (sending.current) return;
    if (id === selected) {
      focusThread();
      return;
    }
    drafts.current.set(selected, { body, photo });
    pending.current = null;
    const draft = drafts.current.get(id);
    setBody(draft?.body ?? '');
    setPhoto(draft?.photo ?? null);
    setSelected(id);
  }
  function changePage(cursor: string, back = false) {
    if (sending.current) return;
    drafts.current.set(selected, { body, photo });
    pending.current = null;
    setBody('');
    setPhoto(null);
    setSelected('');
    setItems([]);
    setNextCursor(null);
    setPreviousCursors((old) => (back ? old.slice(0, -1) : [...old, listCursor]));
    setListCursor(cursor);
  }
  const office = session.user?.role === 'owner' || session.user?.role === 'manager';
  const chat = items.find((c) => c.id === selected);
  // A phone brings the conversation into view. Polls and returning to the
  // selected row do not remount it or replace its unfinished reply/outbox.
  useEffect(() => {
    if (chat) focusThread();
  }, [chat?.id]);
  const visibleChats = items.filter(
    (c) => filter === 'all' || (filter === 'needsReply' ? c.needsReply : c.state === filter),
  );
  const hasPending = !!pending.current || queued.some((p) => p.chatId === chat?.id);
  const canSend = hasPending || !!body.trim() || !!photo;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  function commitList(d: { items: Chat[]; nextCursor?: string | null }) {
    setNextCursor(d.nextCursor ?? null);
    setItems((old) =>
      d.items.map((c) => {
        const previous = old.find((p) => p.id === c.id);
        return previous
          ? {
              ...c,
              hasOlder: previous.hasOlder ?? c.hasOlder,
              messages: [...new Map([...previous.messages, ...c.messages].map((m) => [m.id, m])).values()].sort(
                (a, b) => a.id.localeCompare(b.id),
              ),
              attachments: [...new Map([...previous.attachments, ...c.attachments].map((a) => [a.id, a])).values()],
            }
          : c;
      }),
    );
    if (visitId) setSelected((current) => current || d.items.find((c) => c.appointmentId === visitId)?.id || '');
  }

  function retireDeniedRead(failure: unknown) {
    if (!(
      failure &&
      typeof failure === 'object' &&
      'status' in failure &&
      [401, 403, 404].includes(Number(failure.status))
    ))
      return false;
    // Assignment can be revoked without changing the session fingerprint.
    // Retire loaded data and its gallery; preserve durable unsent device work.
    ++listRequest.current;
    closePhotoViewer();
    setItems([]);
    setNextCursor(null);
    setSelected('');
    drafts.current.clear();
    pending.current = null;
    setBody('');
    setPhoto(null);
    return true;
  }

  function failedLoad(failure: unknown) {
    // Network/5xx failures retain history. Only definitive reads retire it.
    retireDeniedRead(failure);
    setLoadError(true);
  }

  async function readProtected<T>(path: string): Promise<T | null> {
    try {
      return await api.getFresh<T>(path);
    } catch (failure) {
      if (mounted.current && currentPath.current === listPath && retireDeniedRead(failure)) {
        setLoadError(true);
        return null;
      }
      throw failure;
    }
  }

  async function refreshList() {
    const request = ++listRequest.current;
    try {
      const d = await api.getFresh<{ items: Chat[]; nextCursor?: string | null }>(listPath);
      if (!mounted.current || currentPath.current !== listPath || request !== listRequest.current) return;
      commitList(d);
      setLoadError(false);
    } catch (failure) {
      if (mounted.current && currentPath.current === listPath && request === listRequest.current) failedLoad(failure);
    }
  }

  async function runAction(operation: () => Promise<void>, refresh = true) {
    if (sending.current || !mounted.current) return;
    sending.current = true;
    ++listRequest.current; // A pre-mutation poll cannot restore the old state.
    setBusy(true);
    setError('');
    try {
      await operation();
      if (mounted.current && refresh) await refreshList();
    } catch {
      if (mounted.current)
        setError(
          es ? 'No se pudo completar. Inténtelo de nuevo.' : 'Could not complete this action. Please try again.',
        );
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  useEffect(() => {
    let alive = true;
    let polling = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      if (!alive || polling) return;
      if (sending.current) {
        clearTimeout(timer);
        timer = setTimeout(poll, document.hidden ? 60000 : 5000);
        return;
      }
      polling = true;
      clearTimeout(timer);
      const request = ++listRequest.current;
      const current = () => alive && currentPath.current === listPath && request === listRequest.current;
      try {
        // Local pending work must also be available on an initial network
        // failure. Optional device storage cannot hide a successful read.
        const outbox = captureChatOutbox();
        if (outbox.current()) {
          try {
            const rows = await outbox.list();
            if (current() && outbox.current()) setQueued(rows);
          } catch {
            /* Keep network reads usable if device storage fails. */
          }
        }
        if (!current()) return;
        const c = await api.getFresh<{ enabled: boolean; plan: string; monthlyUsed?: number }>('/job-chats/capability');
        if (!current()) return;
        setCap(c);
        if (c.enabled && c.plan !== 'free') {
          const d = await api.getFresh<{ items: Chat[]; nextCursor?: string | null }>(listPath);
          if (!current()) return;
          commitList(d);
        } else setItems([]);
        setLoadError(false);
      } catch (failure) {
        if (current()) failedLoad(failure);
      } finally {
        polling = false;
        if (alive) {
          setLoading(false);
          timer = setTimeout(poll, document.hidden ? 60000 : 5000);
        }
      }
    };
    retryLoad.current = () => void poll();
    void poll();
    document.addEventListener('visibilitychange', poll);
    return () => {
      alive = false;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [listPath]);
  const lastMessageId = chat?.messages.at(-1)?.id;
  useEffect(() => {
    if (!chat || !lastMessageId || !chat.unreadCount || document.hidden) return;
    void api.post(`/job-chats/${chat.id}/read`, { lastReadMessageId: lastMessageId }).catch(() => undefined);
  }, [chat?.id, lastMessageId, chat?.unreadCount]);
  async function send(code?: string) {
    if (!chat?.writable || sending.current || !mounted.current || (!code && !canSend)) return;
    sending.current = true;
    ++listRequest.current;
    const outbox = captureChatOutbox();
    setBusy(true);
    let persisted = false;
    try {
      if (!pending.current) pending.current = queued.find((p) => p.chatId === chat.id) ?? null;
      if (!pending.current)
        pending.current = {
          chatId: chat.id,
          audienceVersion: code
            ? chat.customerParticipation?.version
            : draftAudience.current.has(chat.id)
              ? draftAudience.current.get(chat.id)
              : chat.customerParticipation?.version,
          body: code ? '' : body,
          clientMessageId: crypto.randomUUID(),
          attachmentIds: [],
          code,
          file: code ? null : photo,
          uploadId: crypto.randomUUID(),
        };
      const p = pending.current;
      if (code && p.code !== code) throw new Error('Pending message must be resolved first');
      if (p.chatId !== chat.id) throw new Error('Chat changed');
      if (p.file && p.file.size > 10 * 1024 * 1024) throw new Error('Photo exceeds 10 MB');
      await outbox.save(p);
      persisted = true;
      if (!mounted.current || !outbox.current()) return;
      setQueued((old) => [...old.filter((e) => e.clientMessageId !== p.clientMessageId), p]);
      const rows = await outbox.list();
      if (!mounted.current || !outbox.current()) return;
      setQueued(rows);
      const fresh = await readProtected<Chat>(`/job-chats/${p.chatId}/messages`);
      if (!fresh || !mounted.current || !outbox.current()) return;
      if (!fresh.writable) throw new Error('Chat unavailable');
      if (fresh.customerParticipation)
        setItems((current) =>
          current.map((item) =>
            item.id === p.chatId ? { ...item, customerParticipation: fresh.customerParticipation } : item,
          ),
        );
      if (p.file && !p.attachmentIds.length) {
        const f = new FormData();
        f.set('file', p.file);
        f.set('clientUploadId', p.uploadId);
        if (p.audienceVersion !== undefined) f.set('audienceVersion', String(p.audienceVersion));
        p.attachmentIds = [(await api.upload<{ id: string }>(`/job-chats/${chat.id}/attachments`, f)).id];
      }
      if (!mounted.current || !outbox.current()) return;
      await outbox.save(p);
      if (!mounted.current || !outbox.current()) return;
      await api.post(`/job-chats/${p.chatId}/messages`, {
        body: p.body,
        audienceVersion: p.audienceVersion,
        clientMessageId: p.clientMessageId,
        attachmentIds: p.attachmentIds,
        code: p.code,
      });
      // An acknowledged send retires its durable row even if the user left.
      // UI updates and Done navigation still belong only to this live screen.
      await outbox.remove(p.clientMessageId);
      const remaining = await outbox.list();
      if (!mounted.current || !outbox.current()) return;
      setQueued(remaining);
      pending.current = null;
      if (p.code === 'done' && session.can('schedule:write'))
        navigate(`/schedule?appointment=${chat.appointmentId}&complete=1`);
      if (!p.code) {
        drafts.current.delete(chat.id);
        draftAudience.current.delete(chat.id);
        setBody('');
        setPhoto(null);
      }
      setError('');
      await refreshList();
    } catch (failure) {
      if (!mounted.current || !outbox.current()) return;
      if (!persisted) pending.current = null;
      if (
        failure &&
        typeof failure === 'object' &&
        'status' in failure &&
        typeof failure.status === 'number' &&
        failure.status >= 400 &&
        failure.status < 500 &&
        failure.status !== 429
      )
        pending.current = null;
      setError(
        failure && typeof failure === 'object' && 'code' in failure && failure.code === 'CHAT_AUDIENCE_CHANGED'
          ? es
            ? 'Cambiaron los participantes. Este borrador no se compartió. Descártelo y escriba un mensaje nuevo después de revisar el acceso.'
            : 'Chat participants changed. This draft was not shared. Discard it and write a new message after reviewing access.'
          : persisted
            ? copy.retry
            : es
              ? 'No se guardó. El borrador sigue editable; cambie la foto o libere espacio y reintente.'
              : 'Not saved. Your draft is still editable; replace the photo or free space and retry.',
      );
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  const resume = useRef(() => {});
  resume.current = () => {
    if ((pending.current || queued.some((p) => p.chatId === chat?.id)) && chat?.writable && !busy) void send();
  };
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const online = () => {
      clearTimeout(timer);
      timer = setTimeout(() => resume.current(), 1000);
    };
    window.addEventListener('online', online);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('online', online);
    };
  }, []);
  return (
    <WorkPage
      title={copy.title}
      description={
        es
          ? 'Chats internos del personal para cada visita. Los propietarios y gerentes pueden incluir al cliente.'
          : 'Internal staff chats for each job. Owners and managers can include the customer.'
      }
    >
      {loading && <LoadingBlock />}
      {loadError && (
        <Alert tone="error">
          <p>
            {es
              ? 'No se pudieron actualizar los chats. Inténtelo de nuevo.'
              : 'Could not refresh chats. Please try again.'}
          </p>
          <Button variant="secondary" disabled={busy} onClick={() => retryLoad.current()}>
            {copy.retry}
          </Button>
        </Alert>
      )}
      {error && <Alert tone="error">{error}</Alert>}
      {queued.length > 0 && (
        <aside className="card space-y-3">
          <p>
            {es
              ? 'Pendiente en este dispositivo. Abra el chat para reintentar.'
              : 'Pending on this device. Open the chat to retry.'}
          </p>
          {queued.map((p) => (
            <div key={p.clientMessageId}>
              <button className="btn btn-secondary" disabled={busy} onClick={() => selectChat(p.chatId)}>
                ◷ {p.body || (p.code ? copy[p.code as keyof typeof copy] : copy.pending)}
              </button>
              <button
                className="btn btn-secondary"
                disabled={busy}
                onClick={() =>
                  void runAction(async () => {
                    const outbox = captureChatOutbox();
                    await outbox.remove(p.clientMessageId);
                    const rows = await outbox.list();
                    if (!mounted.current || !outbox.current()) return;
                    setQueued(rows);
                    if (pending.current?.clientMessageId === p.clientMessageId) pending.current = null;
                    if (!p.code) {
                      drafts.current.delete(p.chatId);
                      draftAudience.current.delete(p.chatId);
                    }
                    if (selected === p.chatId) {
                      if (!p.code) {
                        setBody('');
                        setPhoto(null);
                      }
                      setError('');
                    }
                  })
                }
              >
                {es ? 'Descartar' : 'Discard'}
              </button>
            </div>
          ))}
        </aside>
      )}
      {cap?.plan === 'free' && (
        <section className="card space-y-3" aria-label={copy.sample}>
          <p>{copy.sample}</p>
          <article className="work-chat-message">
            <p>{copy.on_my_way}</p>
            <p>{es ? 'Gracias. La entrada está abierta.' : 'Thank you. The entrance is open.'}</p>
            <p>{copy.done}</p>
          </article>
          <Link
            className="font-semibold text-[color:var(--color-brand)] underline"
            to="/settings?source=job_chat_sample#settings-billing"
          >
            {es ? 'Ver Starter' : 'See Starter'}
          </Link>
        </section>
      )}
      {cap && !cap.enabled && cap.plan !== 'free' && (
        <div className="card">
          <WorkEmpty
            title={
              es
                ? 'El chat de visitas aún no está habilitado para su empresa.'
                : 'Job chat is not enabled for your company yet.'
            }
          />
        </div>
      )}
      {cap?.plan === 'starter' && (cap.monthlyUsed ?? 0) >= 150 && (
        <p role="status">
          {es
            ? 'Ya usó 150 chats este mes. Pro no tiene límite mensual.'
            : 'You used 150 job chats this month. Pro has no monthly limit.'}{' '}
          <Link
            className="font-semibold text-[color:var(--color-brand)] underline"
            to="/settings?source=job_chat_cap#settings-billing"
          >
            {es ? 'Ver Pro' : 'See Pro'}
          </Link>
        </p>
      )}
      {cap?.enabled && cap.plan !== 'free' && (
        <>
          <p className="text-sm text-[color:var(--color-muted)]">
            {es
              ? 'El chat abre antes de una visita confirmada según la configuración de su empresa. Complete la visita desde su pantalla habitual.'
              : 'Chat opens before a confirmed visit according to your company’s settings. Complete the visit from its usual screen.'}
          </p>
          {visitId && office && (
            <button
              className="btn btn-secondary"
              disabled={busy}
              onClick={() =>
                void runAction(async () => {
                  const c = await api.post<{ id: string }>(`/job-chats/visits/${visitId}`);
                  if (!mounted.current) return;
                  const d = await readProtected<{ items: Chat[] }>(
                    `${baseListPath}&selectedChatId=${encodeURIComponent(c.id)}`,
                  );
                  if (!d || !mounted.current) return;
                  commitList(d);
                  setSelected(c.id);
                }, false)
              }
            >
              {es ? 'Abrir chat de la visita' : 'Open visit chat'}
            </button>
          )}
          <div className="work-chat-grid" data-empty={items.length === 0} ref={gridRef}>
            <section className="card work-chat-list" aria-label={es ? 'Chats de visitas' : 'Visit chats'}>
              <Select
                label={es ? 'Mostrar chats' : 'Show chats'}
                value={filter}
                disabled={busy}
                onChange={(e) => setFilter(e.target.value)}
                options={[
                  { value: 'all', label: es ? 'Todos' : 'All' },
                  { value: 'open', label: copy.open },
                  { value: 'needsReply', label: es ? 'Necesita respuesta' : 'Needs reply' },
                  { value: 'closing', label: copy.closingState },
                  { value: 'closed', label: copy.closedState },
                ]}
              />
              {!loading && !loadError && visibleChats.length === 0 && (
                <WorkEmpty
                  title={
                    filter === 'all'
                      ? es
                        ? 'Todavía no hay chats aquí'
                        : 'No job chats here yet'
                      : es
                        ? 'Ningún chat coincide'
                        : 'No matching chats'
                  }
                >
                  <p>
                    {filter === 'all'
                      ? es
                        ? 'Los chats de las visitas disponibles aparecerán aquí.'
                        : 'Chats for available visits will appear here.'
                      : es
                        ? 'Cambie el filtro para ver otros chats en esta página.'
                        : 'Change the filter to see other chats on this page.'}
                  </p>
                  {filter !== 'all' && (
                    <Button className="mt-3" disabled={busy} onClick={() => setFilter('all')}>
                      {es ? 'Mostrar todos' : 'Show all'}
                    </Button>
                  )}
                </WorkEmpty>
              )}
              {visibleChats.map((c) => (
                <button
                  className="work-chat-row"
                  id={`job-chat-row-${c.id}`}
                  aria-pressed={c.id === selected}
                  key={c.id}
                  disabled={busy}
                  onClick={() => selectChat(c.id)}
                >
                  <span className="block break-words font-semibold">
                    {c.visitName} {c.unreadCount > 0 ? `(${c.unreadCount})` : ''}
                  </span>
                  <span className="my-1 block text-sm text-[color:var(--color-muted)]">
                    {formatInstantDateTime(c.startsAt, c.timezone ?? fallbackZone)}
                  </span>
                  {
                    copy[
                      c.state === 'closing'
                        ? 'closingState'
                        : c.state === 'closed'
                          ? 'closedState'
                          : c.state === 'archived'
                            ? 'archivedState'
                            : (c.state as 'open')
                    ]
                  }
                </button>
              ))}
              <div className="mt-3 flex flex-wrap gap-2">
                {previousCursors.length > 0 && (
                  <button
                    className="btn btn-secondary"
                    disabled={busy}
                    onClick={() => changePage(previousCursors.at(-1)!, true)}
                  >
                    {es ? 'Chats más recientes' : 'Newer chats'}
                  </button>
                )}
                {nextCursor && (
                  <button className="btn btn-secondary" disabled={busy} onClick={() => changePage(nextCursor)}>
                    {es ? 'Chats anteriores' : 'Older chats'}
                  </button>
                )}
              </div>
            </section>
            {!chat && items.length > 0 && (
              <div className="card">
                <WorkEmpty title={es ? 'Un lugar para cada visita' : 'A place for every visit'}>
                  <p>
                    {es
                      ? 'Seleccione un chat para ver los mensajes y responder.'
                      : 'Choose a chat to read messages and reply.'}
                  </p>
                </WorkEmpty>
              </div>
            )}
            {chat && (
              <WorkSection
                title={chat.visitName}
                actions={
                  <Button className="job-chat-return" variant="secondary" disabled={busy} onClick={returnToChats}>
                    {es ? 'Volver a los chats' : 'Back to chats'}
                  </Button>
                }
                description={
                  <>
                    {formatInstantDateTime(chat.startsAt, chat.timezone ?? fallbackZone)} ·{' '}
                    {chat.state === 'closed'
                      ? copy.closedState
                      : chat.state === 'closing'
                        ? copy.closingState
                        : chat.state === 'archived'
                          ? copy.archivedState
                          : copy.open}
                  </>
                }
              >
                {chat.customerParticipation && (
                  <aside className="mb-4 rounded border p-3">
                    <p className="font-semibold">
                      {chat.customerParticipation.enabled
                        ? es
                          ? 'El cliente tiene acceso a este chat'
                          : 'The customer has access to this chat'
                        : es
                          ? 'Chat interno · Solo personal'
                          : 'Internal chat · Staff only'}
                    </p>
                    <p>
                      {chat.customerParticipation.enabled
                        ? es
                          ? 'El cliente puede ver los mensajes y las fotos marcados como compartidos.'
                          : 'The customer can see messages and photos marked as shared.'
                        : es
                          ? 'El cliente no puede acceder ni recibe avisos de este chat.'
                          : 'The customer cannot access this chat or receive chat notifications.'}
                    </p>
                    {chat.customerParticipation.enabled && chat.customerParticipation.recipientName && (
                      <p>
                        {es ? 'Compartido con: ' : 'Shared with: '}
                        {chat.customerParticipation.recipientName}
                      </p>
                    )}
                    {chat.customerParticipation.needsReview && (
                      <p>
                        {es
                          ? 'Se conservó el acceso anterior. Los correos están pausados hasta que un propietario o gerente revise el acceso.'
                          : 'Existing access was preserved. Email is paused until an owner or manager reviews access.'}
                      </p>
                    )}
                    {office &&
                      (!chat.customerParticipation.enabled || chat.customerParticipation.needsReview) &&
                      chat.writable && (
                        <Button
                          variant="secondary"
                          disabled={busy}
                          onClick={() =>
                            void runAction(async () => {
                              const preview = await api.getFresh<{
                                customerId: string;
                                recipientEmail: string;
                                recipientName: string;
                                historyPolicy: string;
                              }>(`/job-chats/${chat.id}/customer-participation`);
                              if (!mounted.current) return;
                              if (!preview.recipientEmail) {
                                setError(
                                  es
                                    ? 'Añada un correo del cliente o contacto de la visita antes de incluirlo.'
                                    : 'Add a customer or site-contact email before including the customer.',
                                );
                                return;
                              }
                              const scope =
                                preview.historyPolicy === 'existing_customer_history'
                                  ? es
                                    ? 'Se conservará su acceso a los mensajes y fotos anteriores de este chat.'
                                    : 'Their existing access to earlier messages and photos in this chat will remain.'
                                  : es
                                    ? 'Solo verá los mensajes y las fotos enviados después de esta aprobación. El historial interno anterior permanecerá privado.'
                                    : 'They will see only messages and photos sent after this approval. Earlier internal history will remain private.';
                              if (
                                !window.confirm(
                                  `${es ? 'Permitir acceso al chat a' : 'Allow chat access for'} ${preview.recipientName || ''} (${preview.recipientEmail})?\n\n${scope}\n\n${es ? 'Se podrán enviar una invitación por correo y avisos de este chat según la configuración de correo.' : 'A chat invitation email and further chat notifications may be sent according to email settings.'}`,
                                )
                              )
                                return;
                              await api.post(`/job-chats/${chat.id}/customer-participation`, {
                                ...preview,
                                confirmed: true,
                              });
                            })
                          }
                        >
                          {chat.customerParticipation.needsReview
                            ? es
                              ? 'Revisar acceso del cliente'
                              : 'Review customer access'
                            : es
                              ? 'Incluir al cliente en esta visita'
                              : 'Include customer for this job'}
                        </Button>
                      )}
                  </aside>
                )}
                {chat.hasOlder && (
                  <button
                    className="btn btn-secondary"
                    disabled={busy}
                    onClick={() =>
                      void runAction(async () => {
                        const before = chat.messages[0]?.id;
                        if (!before) return;
                        const older = await readProtected<Chat>(
                          `/job-chats/${chat.id}/messages?before=${encodeURIComponent(before)}`,
                        );
                        if (!older || !mounted.current) return;
                        setItems((current) =>
                          current.map((c) =>
                            c.id === chat.id
                              ? {
                                  ...c,
                                  hasOlder: older.hasOlder,
                                  messages: [
                                    ...new Map([...older.messages, ...c.messages].map((m) => [m.id, m])).values(),
                                  ],
                                  attachments: [
                                    ...new Map([...older.attachments, ...c.attachments].map((a) => [a.id, a])).values(),
                                  ],
                                }
                              : c,
                          ),
                        );
                      }, false)
                    }
                  >
                    {es ? 'Mensajes anteriores' : 'Older messages'}
                  </button>
                )}
                {office &&
                  chat.notifications?.map((n) => (
                    <p key={n.event_name}>
                      {chatNotificationText(n, es)}{' '}
                      {n.retryable === true && (
                        <button
                          className="btn btn-secondary"
                          disabled={busy}
                          onClick={() =>
                            void runAction(async () => {
                              await api.post(`/job-chats/${chat.id}/retry-notification`, { event: n.event_name });
                            })
                          }
                        >
                          {es ? 'Reintentar aviso' : 'Retry notification'}
                        </button>
                      )}
                    </p>
                  ))}
                {chat.feedback?.map((f, i) => (
                  <aside key={i} className="rounded border p-3">
                    <h3 className="font-semibold">{copy.feedback}</h3>
                    <p>{f.stars}/5</p>
                    <p>{f.private_feedback}</p>
                  </aside>
                ))}
                {chat.messages.length === 0 && (
                  <WorkEmpty title={es ? 'La conversación empieza aquí' : 'The conversation starts here'} />
                )}
                <div aria-live="polite">
                  {chat.messages.map((m) => (
                    <article className="work-chat-message" data-actor={m.actor_kind} key={m.id}>
                      {chat.customerParticipation?.enabled && m.customerVisible !== undefined && (
                        <small>
                          {m.customerVisible
                            ? es
                              ? 'Compartido con el cliente'
                              : 'Shared with customer'
                            : es
                              ? 'Solo personal'
                              : 'Staff only'}
                        </small>
                      )}
                      <p>
                        <strong>{m.display_name ? `${m.display_name}: ` : ''}</strong>
                        {m.body || (m.system_code ? copy[m.system_code] : '')}
                      </p>
                      {chat.attachments
                        .filter((a) => a.message_id === m.id)
                        .map((a) => (
                          <div key={a.id}>
                            <PhotoTrigger
                              src={`/api/v1/job-chats/${chat.id}/attachments/${a.id}`}
                              alt={copy.photo}
                              group={`chat-${chat.id}`}
                            >
                              <img
                                alt={copy.photo}
                                className="max-h-60 max-w-full rounded-lg"
                                src={`/api/v1/job-chats/${chat.id}/attachments/${a.id}`}
                              />
                            </PhotoTrigger>
                            {office && !a.promoted_file_id && chat.state !== 'archived' && (
                              <button
                                className="btn btn-secondary"
                                disabled={busy}
                                onClick={() =>
                                  void runAction(async () => {
                                    await api.post(`/job-chats/${chat.id}/attachments/${a.id}/promote`);
                                  })
                                }
                              >
                                {es ? 'Guardar en fotos de la visita' : 'Save to visit photos'}
                              </button>
                            )}
                          </div>
                        ))}
                    </article>
                  ))}
                </div>
                {chat.writable ? (
                  <>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(['on_my_way', 'arrived', 'running_late', 'done'] as const).map((code) => (
                        <button
                          className="btn btn-secondary"
                          disabled={busy || !!pending.current || queued.some((p) => p.chatId === chat.id)}
                          key={code}
                          onClick={() => void send(code)}
                        >
                          {copy[code]}
                        </button>
                      ))}
                    </div>
                    <form
                      className="work-chat-composer space-y-3"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void send();
                      }}
                    >
                      <label className="field-label">
                        {es ? 'Mensaje' : 'Message'}
                        <textarea
                          className="field-control mt-1"
                          rows={3}
                          value={body}
                          disabled={busy || !!pending.current || queued.some((p) => p.chatId === chat.id)}
                          maxLength={2000}
                          onChange={(e) => {
                            if (!e.target.value && !photo) draftAudience.current.delete(chat.id);
                            else if (!draftAudience.current.has(chat.id))
                              draftAudience.current.set(chat.id, chat.customerParticipation?.version);
                            setBody(e.target.value);
                          }}
                        />
                      </label>
                      <label className="field-label">
                        {copy.photo}
                        <input
                          className="field-control mt-1 max-w-full"
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          disabled={busy || hasPending}
                          onChange={(e) => {
                            if (!draftAudience.current.has(chat.id))
                              draftAudience.current.set(chat.id, chat.customerParticipation?.version);
                            const file = e.target.files?.[0] ?? null;
                            if (!file && !body) draftAudience.current.delete(chat.id);
                            setPhoto(file);
                          }}
                        />
                      </label>
                      <button className="btn btn-primary" disabled={busy || !canSend}>
                        {busy ? copy.pending : hasPending ? copy.retry : copy.send}
                      </button>
                    </form>
                  </>
                ) : (
                  <p>{chat.state === 'scheduled' ? copy.scheduled : copy.closed}</p>
                )}
                {office && (chat.writable || chat.state === 'closed') && (
                  <button
                    className="btn btn-secondary"
                    disabled={busy || hasPending}
                    onClick={() =>
                      void runAction(async () => {
                        await api.post(`/job-chats/${chat.id}/${chat.writable ? 'close' : 'reopen'}`);
                      })
                    }
                  >
                    {chat.writable
                      ? es
                        ? 'Cerrar chat'
                        : 'Close chat'
                      : es
                        ? 'Reabrir durante 24 horas'
                        : 'Reopen for 24 hours'}
                  </button>
                )}
              </WorkSection>
            )}
          </div>
        </>
      )}
    </WorkPage>
  );
}
